/**
 * "My Tickets" panel — surfaced both inside the MIRA drawer (via the
 * "My tickets" header pill) and on the /profile page.
 *
 * Lists the signed-in user's MIRA-filed tickets, newest first, with
 * category pill, severity pill, screenshot thumb, and a STATUS pill
 * (Open / Resolved / Won't fix). The underlying GitHub issue lives in
 * a private repo the user has no access to — so we deliberately do
 * NOT render any outbound link or issue number.
 */
import { useEffect, useState } from "react";
import { Loader2, Bug, Sparkles, HelpCircle, MoreHorizontal } from "lucide-react";
import { miraMyTickets } from "@/lib/api";

const CATEGORY_META = {
  bug: { label: "Bug", Icon: Bug, color: "text-rose-700 bg-rose-50 border-rose-200" },
  feature: { label: "Idea", Icon: Sparkles, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  question: { label: "Question", Icon: HelpCircle, color: "text-sky-700 bg-sky-50 border-sky-200" },
  other: { label: "Other", Icon: MoreHorizontal, color: "text-slate-700 bg-slate-50 border-slate-200" },
};

const SEVERITY_COLOR = {
  critical: "bg-rose-100 text-rose-800 border-rose-300",
  high: "bg-orange-100 text-orange-800 border-orange-300",
  medium: "bg-amber-100 text-amber-800 border-amber-300",
  low: "bg-slate-100 text-slate-700 border-slate-300",
};

const STATUS_META = {
  open:     { label: "Open",     color: "bg-emerald-50 text-emerald-800 border-emerald-200", dot: "bg-emerald-500" },
  resolved: { label: "Resolved", color: "bg-slate-100 text-slate-700 border-slate-300",      dot: "bg-slate-500" },
  wont_fix: { label: "Won't fix", color: "bg-zinc-100 text-zinc-700 border-zinc-300",        dot: "bg-zinc-400" },
  unknown:  { label: "Pending",  color: "bg-amber-50 text-amber-800 border-amber-200",       dot: "bg-amber-500" },
};

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function MyTicketsPanel({ inline = false }) {
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    miraMyTickets()
      .then((r) => {
        if (!mounted) return;
        setTickets(r?.data?.tickets || []);
      })
      .catch(() => {
        if (mounted) setError("Couldn't load your tickets.");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const wrapClass = inline
    ? "bg-white"
    : "flex-1 overflow-y-auto bg-white";

  return (
    <div className={wrapClass} data-testid="mira-my-tickets-panel">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <h3 className="text-[14px] font-semibold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
          My tickets &amp; issues
        </h3>
        <span className="text-[10.5px] uppercase tracking-wide font-mono text-slate-400">
          {tickets.length} total
        </span>
      </div>

      <div className="px-3 py-2 space-y-2">
        {loading && (
          <div className="flex items-center justify-center py-8 text-slate-500 text-[13px]" data-testid="mira-tickets-loading">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            Loading your tickets…
          </div>
        )}
        {!loading && error && (
          <div className="text-[13px] text-rose-700 px-2 py-3" data-testid="mira-tickets-error">
            {error}
          </div>
        )}
        {!loading && !error && tickets.length === 0 && (
          <div className="text-center py-10 text-slate-500 text-[13px]" data-testid="mira-tickets-empty">
            <div className="text-2xl mb-2 opacity-50">✺</div>
            <div>You haven&apos;t filed any tickets yet.</div>
            <div className="text-[11.5px] text-slate-400 mt-1">
              When you do, they&apos;ll show up here.
            </div>
          </div>
        )}
        {!loading && !error &&
          tickets.map((t) => {
            const meta = CATEGORY_META[t.category] || CATEGORY_META.other;
            const Icon = meta.Icon;
            const status = STATUS_META[t.status] || STATUS_META.unknown;
            const thumbUrl = t.screenshot_id
              ? `${BACKEND}/api/feedback/screenshot/${t.screenshot_id}`
              : null;
            return (
              <div
                key={t.issue_number}
                className="border border-slate-200 rounded-xl px-3 py-2.5 bg-white"
                data-testid={`mira-ticket-${t.issue_number}`}
              >
                <div className="flex items-start gap-2.5">
                  {thumbUrl ? (
                    <img
                      src={thumbUrl}
                      alt=""
                      className="h-10 w-12 object-cover rounded border border-slate-200 shrink-0"
                    />
                  ) : (
                    <div className={`h-10 w-12 rounded grid place-items-center border ${meta.color} shrink-0`}>
                      <Icon className="w-4 h-4" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                      <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${meta.color}`}>
                        {meta.label}
                      </span>
                      <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${SEVERITY_COLOR[t.severity] || SEVERITY_COLOR.medium}`}>
                        {t.severity || "medium"}
                      </span>
                      <span
                        className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${status.color} flex items-center gap-1 ml-auto`}
                        data-testid={`mira-ticket-${t.issue_number}-status`}
                      >
                        <span className={`h-1.5 w-1.5 rounded-full ${status.dot}`} />
                        {status.label}
                      </span>
                    </div>
                    <div className="text-[13px] font-medium text-slate-900 truncate">{t.title}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {timeAgo(t.created_at)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}
